import { queryOptions } from '@tanstack/react-query';
import { restGet } from './supabaseRest';
import { ssrMemo } from './ssrMemo';
export interface PublicGuide {
  id: string; slug: string; title: string; hero_image: string | null;
  html_content: string; brand: string | null; category: string | null;
  meta_description: string | null; schema_json: unknown;
}
export function guidesIndexQueryOptions() {
  return queryOptions({
    queryKey: ['seo-guides-index'],
    // Public data only, with a hard 15-second ceiling (no stale serving).
    // The browser bypasses ssrMemo, retaining normal edit/preview freshness.
    queryFn: () => ssrMemo('seo-guides:public:index', 15_000, async () => {
      const guides: PublicGuide[] = [];
      for (let offset = 0; ; offset += 500) {
        const rows = await restGet<PublicGuide[]>(`seo_guides?select=id,slug,title,hero_image,html_content,brand,category,meta_description,schema_json&is_published=eq.true&include_in_sitemap=eq.true&order=created_at.desc,id.asc&limit=500&offset=${offset}`);
        if (!rows) throw new Error('The guides could not be loaded. Please try again.');
        guides.push(...rows);
        if (rows.length < 500) return guides;
      }
    }), staleTime: 60_000, refetchOnWindowFocus: false,
  });
}
export function guideQueryOptions(slug: string) {
  return queryOptions({
    queryKey: ['seo-guide', slug],
    queryFn: async () => {
      const rows = await ssrMemo(`seo-guides:public:detail:${slug}`, 15_000, async () => {
        const published = await restGet<PublicGuide[]>(`seo_guides?select=id,slug,title,hero_image,html_content,brand,category,meta_description,schema_json&slug=eq.${encodeURIComponent(slug)}&is_published=eq.true&limit=1`);
        if (!published) throw new Error('The guide could not be loaded. Please try again.');
        return published;
      });
      if (rows.length > 0) return rows[0];
      // Draft preview: the public read can't see unpublished rows. Row-level
      // rules let signed-in admins read any guide, so retry authenticated in
      // the browser only (SSR has no user token). Kept as a dynamic import so
      // the vendor-supabase chunk stays out of the normal page path.
      if (typeof document !== 'undefined') {
        const { supabase } = await import('@/integrations/supabase/client');
        const { data } = await supabase
          .from('seo_guides')
          .select('id,slug,title,hero_image,html_content,brand,category,meta_description,schema_json')
          .eq('slug', slug)
          .limit(1);
        const row = data?.[0];
        if (row) return row as PublicGuide;
      }
      return null;
    }, staleTime: 60_000, refetchOnWindowFocus: false,
  });
}
